/**
 * MONSTER TOWN — THE NEIGHBOURS LIVE THEIR DAY. ROUTINE CREW (src/routine/**).
 *
 * "A DAY IN TOWN" §2: *Neighbours run a real schedule: wake and stretch,
 * breakfast, out and about the town, visit the shops, dinner, bath, bed. At
 * night the streets empty and lights go out house by house.*
 *
 * WHAT THIS FILE DOES AND WHAT IT DELIBERATELY DOES NOT
 *  - It reads `src/core/schedule.ts` (pure, data-driven) and sends a neighbour
 *    to the right part of town at the right hour, then gets out of the way.
 *  - It does NOT decide what they do when they get there. That is the brain's
 *    job (`src/npc/brain.ts`), re-weighted by the activity — so a neighbour is
 *    still a monster pottering about, not a train on rails.
 *  - It does NOT own the lights, the sky or the ambience. Those are the world
 *    and audio crews', off `time:phaseChanged`.
 *
 * THE THREE LAWS THIS FILE IS MOST LIKELY TO BREAK, AND HOW IT DOESN'T
 *  LAW #2 — nothing here can be failed, missed or late. A neighbour still
 *    walking to the shop when the shopping hour ends turns round and does the
 *    next thing; there is no arriving late and nothing ever says so.
 *  LAW #1 — a neighbour who has gone in for the night is HIDDEN, not left
 *    standing invisibly in the street where a tap would hit nothing.
 *  PERF — the only per-frame cost is a countdown. The real pass runs every two
 *    seconds over at most seven neighbours, and does nothing at all when the
 *    hour has not moved anybody on.
 *
 * PATHING. Neighbours walk with the same `Walker` the kid's monster uses, which
 * steers and slides and gives up gracefully rather than ever getting stuck — and
 * every destination is checked against the LIVE placement grid AND the world's
 * baked statics first (see `places.ts`), so a kid who builds a wall across the
 * pavement gets a neighbour standing beside the wall, never one inside it and
 * never one frozen — and the pond gets a neighbour on its bank, not in it.
 *
 * ===========================================================================
 * TWO NEIGHBOURS MUST LOOK LIKE TWO NEIGHBOURS
 * ===========================================================================
 * Everything below `spreadSpot` exists because of one capture: five monsters
 * inside a three-metre circle round the fountain, which at the default zoom is
 * not a square with people in it, it is a scuffle. The cause is structural, not
 * random — most tables say `where: 'plaza'` at noon, a place tag resolves to
 * exactly ONE point, and the old per-id offset was an independent hash per
 * monster, so nothing anywhere ever compared two of them.
 *
 * So spots are handed out in one pass, in a stable order, and each one is
 * checked against the ones already given out. It is O(n^2) over at most eight
 * neighbours, twice a second at worst, and it is the only way "spread out" can
 * mean anything.
 *
 * AND THEN THERE IS THE TIDY PASS, because a destination is not a guarantee.
 * The brain (`src/npc/brain.ts`) picks its own short wanders from the placement
 * grid alone, and the walker slides along whatever it bumps into — so a
 * neighbour can still come to rest in the fountain without the director ever
 * sending them there. `tidy()` is the safety net for that: if somebody is
 * standing somewhere no monster can stand, or on top of another monster, they
 * WALK out of it. It never teleports and it never interrupts a walk, so the
 * worst it can ever look like is a monster wandering off — which is what
 * neighbours do anyway.
 *
 * ===========================================================================
 * ROUND 1 OF THE FIX CREW: BOTH HALVES OF THAT SAFETY NET WERE ASLEEP
 * ===========================================================================
 * All of the above shipped, and the next set of captures still had a monster in
 * the fountain basin, one in the pond and three drawn through each other. Two
 * separate reasons, and neither was the idea being wrong:
 *
 *  1. IT RAN TWICE A SECOND. A photograph is not taken on the sweep's beat, and
 *     the walker can slide into the water at any moment in between. The
 *     "standing in something" half is now `tidyStuck` and runs TEN times a
 *     second, on the same tick that already watches for new walks.
 *  2. THE ANTI-HUDDLE HALF WAS UNREACHABLE. It waited for a neighbour who was
 *     standing still, out of a pose and out of timer — a state the brain
 *     consumes within one frame of it existing, while this pass looked twice a
 *     second. In practice it never fired once. It may now interrupt a pose,
 *     rate-limited by `NUDGE_SECONDS`, which is the only way it can ever run.
 *
 * And the question it was asking was too generous by half a metre — see the
 * header of `places.ts`, which is where a monster stopped being a point.
 */

import { bus } from '../core/events'
import { engine } from '../core/engine'
import { clock } from '../core/time'
import { routineAt, type RoutineActivity } from '../core/schedule'
import { world } from '../core/scene'
import type { SceneNode } from '../core/scene'
import type { NpcId, Vec3 } from '../core/types'
import { npcs, townNode } from '../npc/registry'
import { routineForName } from '../npc/roster'
import type { Npc } from '../npc/npc'
import { findWalkPath } from '../world/nav'
import {
  forgetLandmarks,
  homeSpot,
  placeSpot,
  roomyLine,
  roomyNear,
  standable,
  streetSpot,
  walkableNear,
} from './places'

/** How often the whole roster is re-checked against the clock, in seconds. */
const SWEEP_SECONDS = 2
/** Close enough that walking there again would just be fidgeting. */
const ARRIVED = 2.6
/**
 * How far apart two neighbours are AIMED. Measured off the capture that started
 * this: the huddle was 1.2–4.2 m across at the fountain and read as one blob, so
 * the target has to clear the widest of those. A monster is ~1.4 m across at the
 * default zoom.
 */
const APART = 5
/**
 * And how close two of them have to get before the tidy pass steps in. Lower
 * than `APART` on purpose: neighbours drifting near each other is the town being
 * alive, and correcting that would be a director with a clipboard. This is the
 * distance at which two monsters are drawn through each other.
 *
 * RAISED FROM 2.5, because the camera is fixed and isometric. Two monsters 2.5 m
 * apart *along the view axis* are drawn one behind the other with their bodies
 * overlapping — which is what `ui-02-neighbour-request.png` shows at three
 * separate spots, on a frame where the closest world-space pair measured 2.2 m
 * and the old threshold therefore said everything was fine.
 */
const TOO_CLOSE = 3.1
/**
 * The gentlest possible anti-huddle: a neighbour is stepped aside at most once
 * every six seconds. See `tidy()` — the nudge is now allowed to interrupt a
 * pose, so the cooldown is what keeps that from ever becoming a twitch.
 */
const NUDGE_SECONDS = 6
/**
 * And how long before the same monster may be walked out of the pond again. Much
 * shorter than the huddle cooldown, because being inside something is the worse
 * picture — but not zero, or the ten-times-a-second pass can chase a walker that
 * keeps stopping a hair short of the spot it was sent to. See `tidyStuck`.
 */
const RENUDGE_SECONDS = 1.5

/** What a neighbour does the moment they get where they were going. */
const ARRIVE_ACTION: Partial<Record<RoutineActivity, string>> = {
  waking: 'wiggle',
  breakfast: 'eat',
  dinner: 'eat',
  shopping: 'point',
  visiting: 'knock',
  working: 'carry',
  bath: 'clap',
  winddown: 'sit',
  bed: 'knock',
}

/** Activities that mean "be indoors" — the reason the street empties. */
function isIndoors(activity: RoutineActivity): boolean {
  return activity === 'asleep'
}

let sweepIn = 0.5
let installed = false

// ---------------------------------------------------------------------------
// walking ROUND things instead of through them
// ---------------------------------------------------------------------------

/**
 * A DESTINATION IS NOT ENOUGH — the line to it matters just as much.
 *
 * `Walker` steers straight at its target and slides off whatever it bumps into,
 * and it only knows about the placement grid, so a neighbour sent from the
 * square to the shop walks THROUGH the pond and the fountain on the way. A still
 * photograph cannot tell that apart from standing in them, and a still
 * photograph is what this game is judged on: sampling every frame of a midday
 * stretch found neighbours off the ground 7.4% of the time with every
 * destination already correct.
 *
 * The kid's own monster has never had this problem, because `src/world/nav.ts`
 * routes it: A* over the same mask, straightened, walked one leg at a time. That
 * autopilot is keyed to `app.playerPos` and is the player's, so this is the same
 * idea for neighbours, built out of the two pure functions the world crew
 * exports — `findWalkPath`, and `isTownWalkable` via `places.ts`. No mask is
 * built, mutated or refreshed here.
 *
 * IT COVERS WALKS THIS FILE DID NOT ORDER. The brain (`src/npc/brain.ts`) sends
 * neighbours on its own short wanders, and those cut corners too. Rather than
 * reach into another crew's file, `watchWalks` notices any new walk that is
 * about to cross something solid and re-issues it as a route. Ten times a
 * second, over at most eight neighbours, and the check stops at the first
 * unchanged target.
 */
interface Route {
  queue: Vec3[]
  action: string | null
  stopDist: number
}

const routes = new Map<NpcId, Route>()
/** The last walk target seen per neighbour, so a NEW one can be spotted. */
const lastTarget = new Map<NpcId, { x: number; z: number }>()

/** Close enough to a waypoint to move on. Waypoints are guides, not gates. */
const LEG_REACHED = 0.9
/** Shorter than this and routing is not worth it — and cannot loop. */
const ROUTE_MIN = 1.4
/** How often new walks are inspected, in seconds. */
const WATCH_SECONDS = 0.1

let watchIn = 0
/** Seconds of game time since install. Only used to space the tidy nudges. */
let elapsed = 0
/** When each neighbour was last stepped aside, in `elapsed` seconds. */
const lastNudge = new Map<NpcId, number>()

/**
 * Walk there, going round anything in the way. Falls back to the plain straight
 * walk whenever routing has nothing better to offer — a neighbour is never
 * refused a destination (LAW #2).
 */
function sendTo(npc: Npc, spot: Vec3, action: string | null, stopDist: number): void {
  routes.delete(npc.id)
  const from: Vec3 = { x: npc.walker.x, y: 0, z: npc.walker.z }
  lastTarget.set(npc.id, { x: spot.x, z: spot.z })
  /*
   * `roomyLine`, NOT `lineIsClear`: the world's own test only promises that a
   * PATH fits, and a monster is wider than its path. Asking for a body's width
   * here is what stops a neighbour wading through the fountain on a walk that
   * the mask was perfectly happy with. See the header of `places.ts`.
   */
  if (Math.hypot(spot.x - from.x, spot.z - from.z) < ROUTE_MIN || roomyLine(from, spot)) {
    npc.walkTo(spot, action, stopDist)
    return
  }
  const path = findWalkPath(from, spot)
  if (path === null || path.length < 3) {
    npc.walkTo(spot, action, stopDist)
    return
  }
  // A* corners are cut to the mask's own clearance. Push each waypoint out to
  // where a whole monster fits, so the LEGS between them keep their distance.
  const queue = path.slice(1).map((p, i, all) => (i === all.length - 1 ? p : roomyNear(p)))
  const first = queue.shift()
  if (first === undefined || queue.length === 0) {
    npc.walkTo(spot, action, stopDist)
    return
  }
  routes.set(npc.id, { queue, action, stopDist })
  npc.walkTo(first, null, 0.6)
}

/** Step a routed neighbour onto its next waypoint. Cheap; usually empty. */
function advanceRoutes(): void {
  if (routes.size === 0) return
  for (const [id, r] of routes) {
    const npc = npcs.get(id)
    if (npc === undefined || npc.scripted || npc.indoors) {
      routes.delete(id)
      continue
    }
    const t = npc.walker.target
    const d = Math.hypot(npc.walker.x - t.x, npc.walker.z - t.z)
    // `walking` going false covers the walker giving up as well as arriving —
    // either way the leg is over and standing still is never the answer.
    if (npc.walking && d > LEG_REACHED) continue
    const next = r.queue.shift()
    if (next === undefined) {
      routes.delete(id)
      continue
    }
    if (r.queue.length === 0) {
      routes.delete(id)
      lastTarget.set(id, { x: next.x, z: next.z })
      npc.walkTo(next, r.action, r.stopDist)
    } else {
      npc.walkTo(next, null, 0.6)
    }
  }
}

/**
 * Catch a walk somebody else started that is about to cross something solid — or
 * that ENDS somewhere a monster cannot stand.
 *
 * The second half is the one that was missing, and it is where the pond and the
 * fountain came from. `src/npc/brain.ts` picks its short wanders with
 * `roamPoint`, which reads the placement grid and nothing else — the baked
 * statics are invisible to it, so "a random walkable tile near me" is regularly
 * open water. The line test alone let those through whenever the water happened
 * to be a straight clear walk away, which by the pond it usually is.
 */
/**
 * Everybody actually out in the town right now. Reuses one array: this runs ten
 * times a second on the hand-me-down iPad and a fresh array each pass is a
 * garbage-collection pause nobody asked for.
 */
const scratch: Npc[] = []
function visibleInTown(node: SceneNode): readonly Npc[] {
  const townId = node.def.id
  scratch.length = 0
  for (const npc of npcs.values()) {
    if (npc.sceneId !== townId || npc.scripted || npc.indoors) continue
    scratch.push(npc)
  }
  return scratch
}

function watchWalks(node: SceneNode, here: readonly Npc[]): void {
  for (const npc of here) {
    if (!npc.walking) {
      lastTarget.delete(npc.id)
      continue
    }
    if (routes.has(npc.id)) continue
    const t = npc.walker.target
    const prev = lastTarget.get(npc.id)
    if (prev !== undefined && Math.abs(prev.x - t.x) < 0.02 && Math.abs(prev.z - t.z) < 0.02) {
      continue
    }
    lastTarget.set(npc.id, { x: t.x, z: t.z })
    const from: Vec3 = { x: npc.walker.x, y: 0, z: npc.walker.z }
    if (Math.hypot(t.x - from.x, t.z - from.z) < ROUTE_MIN) continue
    const dest = standable(node.grid, t.x, t.z) ? t : walkableNear(node.grid, t)
    const moved = Math.hypot(dest.x - t.x, dest.z - t.z) > 0.05
    // `roomyLine`, for the same reason `sendTo` uses it: a brain wander that
    // merely FITS through the gap by the pond still photographs as a monster in
    // the water on the way past.
    if (!moved && roomyLine(from, t)) continue
    // `sendTo` records the corrected target, so the next pass sees no change and
    // this cannot chase its own tail.
    sendTo(npc, dest, npc.onArrive, npc.walker.arriveDist)
  }
}

// ---------------------------------------------------------------------------
// one neighbour
// ---------------------------------------------------------------------------

function routineIdOf(npc: Npc): string {
  if (npc.routineId === null) {
    // The archetype names it; a restored neighbour gets the same answer from
    // their name, so no save had to change to give the town a rhythm.
    npc.routineId = npc.archetype.routine !== '' ? npc.archetype.routine : routineForName(npc.name)
  }
  return npc.routineId
}

/** Spots already handed out this pass. Cleared at the top of every sweep. */
type Claims = Vec3[]

/** Is `p` clear of everybody already placed this pass? */
function clearOf(p: Vec3, taken: Claims, want: number): boolean {
  for (const q of taken) {
    if (Math.hypot(p.x - q.x, p.z - q.z) < want) return false
  }
  return true
}

/** The golden angle. Successive multiples never repeat a direction. */
const GOLDEN = Math.PI * (3 - Math.sqrt(5))

/**
 * THREE MONSTERS AT THE SHOP SHOULD LOOK LIKE THREE MONSTERS AT THE SHOP, not
 * like one monster drawn three times.
 *
 * `shots/routine/01-midday-street.png` showed the whole roster stacked on the
 * fountain tile at noon, because every townsfolk table says `where: 'plaza'` and
 * a place resolves to exactly one cell.
 *
 * Each neighbour still starts from their OWN bearing, hashed from their id, so
 * the same monster takes the same side of the fountain every day. What is new is
 * that the bearing is a starting point rather than an answer: it steps round by
 * the golden angle, widening as it goes, until it finds somewhere that is both
 * somewhere a monster can stand and clear of everyone placed before them.
 *
 * THE CANDIDATES ARE TESTED, NOT SNAPPED. This used to run every candidate
 * through `walkableNear` and then throw away the ones that came back unusable —
 * fourteen ring searches to pick one spot. That was survivable while a
 * standability test was two array reads; once it grew a body ring it was the
 * most expensive thing in the frame, and the whole point of the ring is that it
 * gets asked a lot. Testing the candidate directly is the same answer for a tenth
 * of the work: a candidate that is not standable is simply the wrong candidate,
 * and there are thirteen more behind it plus the `walkableNear` at the bottom.
 */
function spreadSpot(node: SceneNode, npc: Npc, at: Vec3, taken: Claims): Vec3 {
  let h = 0
  for (let i = 0; i < npc.id.length; i++) h = (h * 31 + npc.id.charCodeAt(i)) >>> 0
  const base = (h % 360) * (Math.PI / 180)
  let fallback: Vec3 | null = null
  for (let i = 0; i < 14; i++) {
    const a = base + i * GOLDEN
    const r = 2.6 + Math.floor(i / 5) * 2.2 + (h % 5) * 0.3
    const p: Vec3 = { x: at.x + Math.cos(a) * r, y: 0, z: at.z + Math.sin(a) * r }
    if (!standable(node.grid, p.x, p.z)) continue
    if (fallback === null) fallback = p
    if (clearOf(p, taken, APART)) return p
  }
  // Nowhere within reach is both free and standable — a very built-up town, or
  // a very popular corner. Somewhere a monster can STAND always wins over
  // somewhere roomy: overlapping art is untidy, art inside a wall is a bug.
  return fallback ?? walkableNear(node.grid, at)
}

/** Where this neighbour should be standing, right now. Null = anywhere. */
function targetSpot(node: SceneNode, npc: Npc, where: string | null, taken: Claims): Vec3 | null {
  if (where === null) return null
  if (where === 'home') {
    // A home is already one address per neighbour; spreading it would move it.
    if (npc.homeSpot === null) npc.homeSpot = homeSpot(node, npc.id)
    return npc.homeSpot
  }
  if (where === 'street') {
    return spreadSpot(node, npc, streetSpot(node, npc.id, Math.floor(clock.hour)), taken)
  }
  const p = placeSpot(node, where)
  return p === null ? null : spreadSpot(node, npc, p, taken)
}

/**
 * Put one neighbour where the hour says they should be.
 *
 * `snap` teleports instead of walking — used when the kid is not looking (a
 * different scene) and after a sleep jump, because seven monsters trudging
 * across town at 7am the morning after a nap is not a rhythm, it is a commute.
 */
function apply(node: SceneNode, npc: Npc, hour: number, snap: boolean, taken: Claims): void {
  if (npc.scripted) return // the venue or the tutorial is steering them
  const r = routineAt(routineIdOf(npc), hour)
  if (r === null) return

  const changed = npc.routine !== r.activity
  npc.routine = r.activity

  const inside = isIndoors(r.activity)
  const spot = targetSpot(node, npc, r.where, taken)
  /*
   * A NEIGHBOUR ONLY TAKES UP ROOM IF THEY ARE GOING TO BE VISIBLE. Somebody
   * asleep behind their own front door is not standing in the square, so their
   * doorstep must not push a monster who IS out of the way. A neighbour with no
   * resolvable place claims the ground they are already on, for the same reason.
   */
  if (!inside) taken.push(spot ?? { x: npc.walker.x, y: 0, z: npc.walker.z })

  if (inside) {
    if (spot !== null && !snap) {
      const d = Math.hypot(spot.x - npc.walker.x, spot.z - npc.walker.z)
      if (d > ARRIVED) {
        // Walk home first; the sweep will notice they arrived and tuck them in.
        if (changed || !npc.walking) sendTo(npc, spot, 'knock', 0.5)
        npc.setIndoors(false)
        return
      }
    }
    if (spot !== null && snap) {
      routes.delete(npc.id)
      npc.setPosition(spot.x, spot.z)
    }
    if (!npc.indoors) {
      routes.delete(npc.id)
      npc.walker.stop()
      npc.stopActing()
      npc.setIndoors(true)
      // A little puff so a monster never simply blinks out of existence.
      bus.emit('fx:play', { effect: 'poof', at: { ...npc.position, y: 0.6 }, count: 6 })
    }
    return
  }

  if (npc.indoors) {
    npc.setIndoors(false)
    bus.emit('fx:play', { effect: 'poof', at: { ...npc.position, y: 0.6 }, count: 6 })
    npc.act('wiggle')
    npc.timer = 1.6
  }

  if (spot === null) return
  const d = Math.hypot(spot.x - npc.walker.x, spot.z - npc.walker.z)
  if (snap) {
    // A teleport is not a walk, so any route in flight is over.
    routes.delete(npc.id)
    npc.setPosition(spot.x, spot.z)
    return
  }
  if (!changed) return
  if (d <= ARRIVED) return
  sendTo(npc, spot, ARRIVE_ACTION[r.activity] ?? 'lookAround', 0.6)
  // A walk across town is allowed to take as long as it takes; the brain leaves
  // them alone until the timer runs out, and the walker never gets stuck.
  npc.timer = Math.min(20, 3 + d * 0.35)
}

// ---------------------------------------------------------------------------
// the sweep
// ---------------------------------------------------------------------------

/**
 * THE TIDY PASS — the safety net described in the header.
 *
 * Two things, both of which are only ever true because somebody ELSE moved this
 * monster: standing where no monster can stand, and standing inside another
 * monster. Neither is corrected by teleporting and neither interrupts anything:
 * a neighbour who is walking, or mid-pose, or being steered by another crew, is
 * left completely alone.
 *
 * `out` is every visible neighbour's position, already gathered by the sweep, so
 * this is one pass over at most eight of them.
 */
function tidy(node: SceneNode, out: readonly Npc[]): number {
  let nudged = tidyStuck(node, out)
  for (const npc of out) {
    if (npc.scripted || npc.indoors || npc.walking) continue
    // Mid-route, between two waypoints: they are going somewhere already.
    if (routes.has(npc.id)) continue
    const here: Vec3 = { x: npc.walker.x, y: 0, z: npc.walker.z }
    if (!standable(node.grid, here.x, here.z)) continue // `tidyStuck` has it
    /*
     * =====================================================================
     * WHY THIS BRANCH HAD NEVER ONCE RUN, WHICH IS WHY THEY STILL BUNCH UP
     * =====================================================================
     * It used to skip anybody with `busyActing` or a timer left, and wait for a
     * neighbour standing still with nothing on. That state does not exist for
     * longer than a single frame: `src/npc/brain.ts` runs every fixed step and
     * its first act on finding an idle neighbour with an expired timer is to
     * give them something to do and a new timer. The sweep here runs twice a
     * second. So the anti-huddle pass was, in practice, unreachable — which is
     * exactly what "neighbours bunch up" being reported as still-not-fixed with
     * a whole tidy pass in the file was trying to tell us.
     *
     * A pose IS interruptible now. Two monsters drawn through each other is a
     * worse picture than one of them wandering off mid-yawn, and a neighbour
     * strolling a few steps is the most ordinary thing in this game. The
     * `NUDGE_SECONDS` cooldown is what stops it becoming a fidget, and a walk is
     * still never interrupted.
     */
    if (elapsed - (lastNudge.get(npc.id) ?? -Infinity) < NUDGE_SECONDS) continue
    let crowd: Vec3 | null = null
    for (const other of out) {
      if (other === npc || other.indoors) continue
      const d = Math.hypot(other.walker.x - here.x, other.walker.z - here.z)
      if (d < TOO_CLOSE) {
        crowd = { x: other.walker.x, y: 0, z: other.walker.z }
        break
      }
    }
    if (crowd === null) continue
    const away = Math.atan2(here.z - crowd.z, here.x - crowd.x)
    const step = stepAside(node, here, away, out, npc)
    if (step === null) continue
    npc.stopActing()
    sendTo(npc, step, 'lookAround', 0.4)
    npc.timer = 3
    lastNudge.set(npc.id, elapsed)
    nudged++
  }
  return nudged
}

/**
 * IN THE POND. Walk out of it — `walkableNear` finds the bank, and the pose is
 * dropped because a monster napping in a fountain is exactly the bug.
 *
 * SPLIT OUT AND RUN TEN TIMES A SECOND, not twice. The sweep's two-second beat
 * is right for "the hour has moved somebody on" and far too slow for this: the
 * walker slides along whatever it bumps into, so a neighbour can come to rest in
 * the water at any moment, and a still photograph taken in the intervening two
 * seconds is a monster standing in a pond. It costs one `standable` per visible
 * neighbour — nine array reads each, at most eight of them — and does nothing at
 * all in the overwhelmingly common case where everybody is on dry land.
 */
function tidyStuck(node: SceneNode, out: readonly Npc[]): number {
  let nudged = 0
  for (const npc of out) {
    if (npc.scripted || npc.indoors || npc.walking) continue
    if (routes.has(npc.id)) continue
    /*
     * ONE CORRECTION AT A TIME, and it is not optional politeness — it is what
     * stops this becoming a hot loop.
     *
     * The walker stops `arriveDist` SHORT of its target. Send somebody to the
     * one standable cell nearby with the usual half-metre stop distance and they
     * come to rest half a metre off it, which can easily be somewhere that is
     * still not standable — so the next tick sends them again, and the tick after
     * that, ten times a second, each one costing a `walkableNear` and often an
     * A* route. Measured as a whole-tool stall. Two things fix it: land ON the
     * spot (`0.12`), and never re-nudge the same monster inside `RENUDGE`.
     */
    if (elapsed - (lastNudge.get(npc.id) ?? -Infinity) < RENUDGE_SECONDS) continue
    const here: Vec3 = { x: npc.walker.x, y: 0, z: npc.walker.z }
    if (standable(node.grid, here.x, here.z)) continue
    const dry = walkableNear(node.grid, here)
    // Nowhere better to be: leave them alone rather than shuffle on the spot.
    if (Math.hypot(dry.x - here.x, dry.z - here.z) < 0.25) continue
    npc.stopActing()
    sendTo(npc, dry, 'lookAround', 0.12)
    npc.timer = 3
    lastNudge.set(npc.id, elapsed)
    nudged++
  }
  return nudged
}

/** A standable spot roughly `APART` away on `bearing`, or null if there is none. */
function stepAside(
  node: SceneNode,
  from: Vec3,
  bearing: number,
  out: readonly Npc[],
  self: Npc,
): Vec3 | null {
  const others: Claims = []
  for (const o of out) {
    if (o !== self && !o.indoors) others.push({ x: o.walker.x, y: 0, z: o.walker.z })
  }
  for (let i = 0; i < 8; i++) {
    // Straight away from them first, then further round, then further out.
    const a = bearing + (i % 4) * (Math.PI / 4) * (i % 2 === 0 ? 1 : -1)
    const r = APART * (1 + Math.floor(i / 4) * 0.5)
    // Tested, not snapped — same reason as `spreadSpot`.
    const p: Vec3 = { x: from.x + Math.cos(a) * r, y: 0, z: from.z + Math.sin(a) * r }
    if (!standable(node.grid, p.x, p.z)) continue
    if (Math.hypot(p.x - from.x, p.z - from.z) < 0.5) continue
    if (clearOf(p, others, TOO_CLOSE)) return p
  }
  return null
}

function sweep(snap = false): number {
  const node = townNode()
  if (node === null) return 0
  const hour = clock.hour
  const townId = node.def.id
  // The town is not on screen: the kid is in a room. Nobody is watching the
  // street, so put everyone where they belong rather than making them walk it.
  const unseen = snap || world.activeSceneId !== townId
  let moved = 0
  /*
   * A STABLE ORDER, so the same monster gets first pick of the fountain every
   * time. Map iteration order is insertion order, which is the order neighbours
   * happened to spawn or be restored in — spots would silently rearrange
   * themselves after a reload, and the spread is only worth anything if it is
   * the same spread tomorrow.
   */
  const here: Npc[] = []
  for (const npc of npcs.values()) {
    /*
     * ONLY NEIGHBOURS WHO ARE ACTUALLY OUT IN THE TOWN.
     *
     * A neighbour standing in the ice cream shop is there because the VENUE
     * asked for them, and their coordinates are that room's, not the town's.
     * Placing them on a town spot would teleport a customer through a wall — and
     * a routine has no business steering somebody another crew borrowed.
     */
    if (npc.sceneId !== townId) continue
    here.push(npc)
  }
  here.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))

  const taken: Claims = []
  for (const npc of here) {
    const before = npc.routine
    apply(node, npc, hour, unseen, taken)
    if (before !== npc.routine) moved++
  }
  tidy(node, here)
  return moved
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

export function installDirector(): void {
  if (installed) return
  installed = true

  // THE SCHEDULE MOVED SOMEBODY ON. `pumpRoutines` fires this per table, so one
  // event can cover several neighbours; the sweep sorts out who.
  bus.on('routine:changed', () => {
    sweepIn = 0
  })

  // A new day, and the morning after the bed. Both are jumps, so everybody is
  // placed rather than sent walking.
  bus.on('time:dayStarted', () => sweep(true))
  bus.on('time:slept', () => sweep(true))

  // A different town (or a fresh plot) has different doors.
  bus.on('save:loaded', () => {
    forgetLandmarks()
    routes.clear()
    lastTarget.clear()
    lastNudge.clear()
    for (const npc of npcs.values()) {
      npc.routine = null
      npc.routineId = null
      npc.homeSpot = null
    }
    sweepIn = 0.25
  })

  // Somebody just moved in — give them today, not day one.
  bus.on('npc:spawned', () => {
    sweepIn = Math.min(sweepIn, 0.25)
  })

  // Walking back into town at nine at night should show a night-time town.
  bus.on('scene:entered', () => {
    sweepIn = Math.min(sweepIn, 0.2)
  })

  engine.addSystem({
    id: 'routine.director',
    order: 41,
    fixed: (dt) => {
      /*
       * THE PER-FRAME HALF, and it is deliberately almost nothing: stepping a
       * routed neighbour onto their next waypoint returns immediately while no
       * route is open (the common case), and new walks are only inspected ten
       * times a second. The two-second sweep below is unchanged.
       */
      advanceRoutes()
      elapsed += dt
      watchIn -= dt
      if (watchIn <= 0) {
        watchIn = WATCH_SECONDS
        const node = townNode()
        if (node !== null && world.activeSceneId === node.def.id) {
          const here = visibleInTown(node)
          watchWalks(node, here)
          // The safety net, at the cadence a photograph is taken at.
          tidyStuck(node, here)
        }
      }
      sweepIn -= dt
      if (sweepIn > 0) return
      sweepIn = SWEEP_SECONDS
      sweep(false)
    },
  })
}

// ---------------------------------------------------------------------------
// debug / QA surface
// ---------------------------------------------------------------------------

export interface RoutineReport {
  id: string
  name: string
  routineId: string
  activity: string
  where: string | null
  indoors: boolean
  x: number
  z: number
}

/** What every neighbour is doing right now. Read by `tools/routine-check.mjs`. */
export function routineReport(): RoutineReport[] {
  const hour = clock.hour
  return [...npcs.values()].map((npc) => {
    const r = routineAt(routineIdOf(npc), hour)
    return {
      id: npc.id,
      name: npc.name,
      routineId: routineIdOf(npc),
      activity: r?.activity ?? 'idle',
      where: r?.where ?? null,
      indoors: npc.indoors,
      x: npc.walker.x,
      z: npc.walker.z,
    }
  })
}

/** Force the sweep now (QA only). Returns how many neighbours changed activity. */
export function sweepNow(snap = true): number {
  return sweep(snap)
}

/**
 * WHY IS THAT ONE IN THE FOUNTAIN? (QA only — prints, decides nothing.)
 *
 * Every reason the tidy pass has for leaving a neighbour where they are, per
 * neighbour, in one read. Written because "somebody is still in the water" is
 * four different bugs wearing the same photograph: they are walking, they are
 * mid-route, another crew has them scripted, or there is genuinely nowhere
 * better within reach.
 */
export interface StuckReport {
  id: string
  name: string
  x: number
  z: number
  standable: boolean
  walking: boolean
  scripted: boolean
  indoors: boolean
  routed: boolean
  busy: boolean
  timer: number
  sinceNudge: number
  /** Where `walkableNear` would send them, and how far that is. */
  outTo: { x: number; z: number; d: number } | null
}

export function stuckReport(): StuckReport[] {
  const node = townNode()
  if (node === null) return []
  const out: StuckReport[] = []
  for (const npc of npcs.values()) {
    if (npc.sceneId !== node.def.id) continue
    const ok = standable(node.grid, npc.walker.x, npc.walker.z)
    const dry = ok ? null : walkableNear(node.grid, { x: npc.walker.x, y: 0, z: npc.walker.z })
    out.push({
      id: npc.id,
      name: npc.name,
      x: Number(npc.walker.x.toFixed(2)),
      z: Number(npc.walker.z.toFixed(2)),
      standable: ok,
      walking: npc.walking,
      scripted: npc.scripted,
      indoors: npc.indoors,
      routed: routes.has(npc.id),
      busy: npc.busyActing,
      timer: Number(npc.timer.toFixed(2)),
      sinceNudge: Number((elapsed - (lastNudge.get(npc.id) ?? -999)).toFixed(2)),
      outTo:
        dry === null
          ? null
          : {
              x: Number(dry.x.toFixed(2)),
              z: Number(dry.z.toFixed(2)),
              d: Number(Math.hypot(dry.x - npc.walker.x, dry.z - npc.walker.z).toFixed(2)),
            },
    })
  }
  return out
}
